import mapboxgl from 'mapbox-gl';
import { Coordinates } from './types';

const CIRCLE_COLOR = '#d1d5db';

export const addCircle = (
  mapInstance: mapboxgl.Map,
  id: string,
  center: [number, number],
  radiusMeters: number
): void => {
  const points = 64;
  const coords: [number, number][] = [];
  const km = radiusMeters / 1000;

  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = km * Math.cos(angle);
    const dy = km * Math.sin(angle);
    const lat = center[1] + (dy / 111.32);
    const lng = center[0] + (dx / (111.32 * Math.cos(center[1] * Math.PI / 180)));
    coords.push([lng, lat]);
  }
  coords.push(coords[0]);

  if (mapInstance.getLayer(`${id}-fill`)) mapInstance.removeLayer(`${id}-fill`);
  if (mapInstance.getLayer(`${id}-stroke`)) mapInstance.removeLayer(`${id}-stroke`);
  if (mapInstance.getSource(id)) mapInstance.removeSource(id);

  mapInstance.addSource(id, {
    type: 'geojson',
    data: { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } }
  });

  mapInstance.addLayer({
    id: `${id}-fill`,
    type: 'fill',
    source: id,
    paint: { 'fill-color': CIRCLE_COLOR, 'fill-opacity': 0.25 }
  });

  mapInstance.addLayer({
    id: `${id}-stroke`,
    type: 'line',
    source: id,
    paint: { 'line-color': '#9ca3af', 'line-width': 2, 'line-opacity': 0.8 }
  });
};

export const addRouteMarkers = (
  mapInstance: mapboxgl.Map,
  start: Coordinates,
  end: Coordinates,
  markerClass: string,
  markersRef: React.MutableRefObject<mapboxgl.Marker[]>
): void => {
  const createPinSvg = (label: string) => `
    <svg width="32" height="44" viewBox="0 0 32 44" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.163 0 0 7.163 0 16C0 27 16 44 16 44S32 27 32 16C32 7.163 24.837 0 16 0Z" fill="#0044d7"/>
      <text x="16" y="17" text-anchor="middle" dominant-baseline="middle" fill="white" font-weight="bold" font-size="14" font-family="sans-serif">${label}</text>
    </svg>
  `;

  const markerAEl = document.createElement('div');
  markerAEl.className = markerClass;
  markerAEl.innerHTML = createPinSvg('A');

  const markerA = new mapboxgl.Marker({ element: markerAEl, anchor: 'bottom' })
    .setLngLat([start.lng, start.lat])
    .addTo(mapInstance);
  markersRef.current.push(markerA);

  const markerBEl = document.createElement('div');
  markerBEl.className = markerClass;
  markerBEl.innerHTML = createPinSvg('B');

  const markerB = new mapboxgl.Marker({ element: markerBEl, anchor: 'bottom' })
    .setLngLat([end.lng, end.lat])
    .addTo(mapInstance);
  markersRef.current.push(markerB);
};

export const fetchAndDrawRoute = async (
  mapInstance: mapboxgl.Map,
  start: [number, number],
  end: [number, number],
  accessToken: string,
  onDistanceCalculated?: (distanceKm: number) => void
): Promise<void> => {
  try {
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson&access_token=${accessToken}`;
    const response = await fetch(url);
    const data = await response.json();

    if (data.routes?.[0] && mapInstance) {
      const distanceKm = Math.round(data.routes[0].distance / 1000);
      onDistanceCalculated?.(distanceKm);

      if (mapInstance.getLayer('template-route-layer')) mapInstance.removeLayer('template-route-layer');
      if (mapInstance.getSource('template-route')) mapInstance.removeSource('template-route');

      mapInstance.addSource('template-route', {
        type: 'geojson',
        data: { type: 'Feature', properties: {}, geometry: data.routes[0].geometry }
      });

      mapInstance.addLayer({
        id: 'template-route-layer',
        type: 'line',
        source: 'template-route',
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: { 'line-color': 'rgba(0, 68, 215, 0.5)', 'line-width': 4 }
      });
    }
  } catch (err) {
    console.error('Failed to fetch route:', err);
  }
};

export const setupClusterLayers = (
  mapInstance: mapboxgl.Map,
  sourceName: string
): void => {
  mapInstance.addLayer({
    id: 'clusters',
    type: 'circle',
    source: sourceName,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#4b5563',
      'circle-radius': [
        'interpolate', ['linear'], ['zoom'],
        3, ['step', ['get', 'point_count'], 20, 10, 24, 50, 28],
        5, ['step', ['get', 'point_count'], 18, 10, 22, 50, 26],
        8, ['step', ['get', 'point_count'], 16, 10, 20, 50, 24],
        12, ['step', ['get', 'point_count'], 20, 10, 24, 50, 28]
      ],
      'circle-stroke-width': 3,
      'circle-stroke-color': '#fff',
      'circle-opacity': 1
    }
  });

  mapInstance.addLayer({
    id: 'cluster-count',
    type: 'symbol',
    source: sourceName,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': ['interpolate', ['linear'], ['zoom'], 3, 12, 5, 11, 8, 11, 12, 12]
    },
    paint: { 'text-color': '#ffffff' }
  });

  mapInstance.addLayer({
    id: 'unclustered-point',
    type: 'circle',
    source: sourceName,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': '#4b5563',
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 10, 5, 9, 8, 8, 12, 10],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
      'circle-opacity': 1
    }
  });
};

export const setupClusterInteractions = (
  mapInstance: mapboxgl.Map,
  sourceName: string
): void => {
  mapInstance.on('dblclick', 'clusters', (e) => {
    e.preventDefault();
    const features = mapInstance.queryRenderedFeatures(e.point, { layers: ['clusters'] });
    const clusterId = features[0]?.properties?.cluster_id;
    if (!clusterId) return;

    const source = mapInstance.getSource(sourceName) as mapboxgl.GeoJSONSource;
    const geometry = features[0].geometry as GeoJSON.Point;
    const coordinates = geometry.coordinates.slice() as [number, number];

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      mapInstance.easeTo({ center: coordinates, zoom: zoom! });
    });
  });

  mapInstance.on('mouseenter', 'clusters', () => {
    mapInstance.getCanvas().style.cursor = 'pointer';
  });
  mapInstance.on('mouseleave', 'clusters', () => {
    mapInstance.getCanvas().style.cursor = '';
  });
  mapInstance.on('mouseenter', 'unclustered-point', () => {
    mapInstance.getCanvas().style.cursor = 'pointer';
  });
  mapInstance.on('mouseleave', 'unclustered-point', () => {
    mapInstance.getCanvas().style.cursor = '';
  });
};
